import { useNavigate } from "react-router-dom";
import {
  Phone,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
  MapPin,
  ArrowUp,
  Heart
} from "lucide-react";
import { Button } from "./ui/button";

export const Footer = () => {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  };

  return (
    <footer className="bg-brand-navy text-white pt-20 pb-8 relative overflow-hidden">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-brand-blue/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2 pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-teal/10 rounded-full blur-3xl -translate-x-1/2 translate-y-1/2 pointer-events-none delay-700"></div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Brand Section */}
          <div className="space-y-6">
            <div
              className="inline-block cursor-pointer pb-2 group relative"
              onClick={() => navigate('/')}
            >
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-white/40 blur-2xl rounded-full opacity-60 group-hover:opacity-100 transition-opacity duration-500"></div>

              <img loading="lazy" decoding="async"
                src="/logo.png"
                alt="BharatHeal"
                className="relative h-14 md:h-16 w-auto object-contain drop-shadow-[0_0_25px_rgba(255,255,255,0.5)] group-hover:drop-shadow-[0_0_40px_rgba(255,255,255,0.9)] transition-all duration-300"
              />
            </div>
            <p className="text-gray-400 leading-relaxed">
              Connecting international patients with India's world-class healthcare. Your trusted partner for medical tourism in India, ensuring quality care and seamless experiences.
            </p>
            <div className="flex gap-4 pt-2">
              {[
                { icon: Facebook, href: "https://facebook.com" },
                { icon: Twitter, href: "https://twitter.com" },
                { icon: Instagram, href: "https://instagram.com" },
                { icon: Linkedin, href: "https://linkedin.com" }
              ].map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-brand-saffron hover:text-brand-navy transition-all duration-300 hover:scale-110"
                >
                  <social.icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white border-b border-white/10 pb-2 inline-block">Quick Links</h4>
            <ul className="space-y-4">
              {[
                { name: "Home", path: "/" },
                { name: "About India", path: "/about" },
                { name: "Treatments", path: "/treatments" },
                { name: "Our Services", path: "/services" },
                { name: "Contact Us", path: "/contact" }
              ].map((link, index) => (
                <li key={index}>
                  <button
                    onClick={() => navigate(link.path)}
                    className="text-gray-400 hover:text-brand-saffron hover:translate-x-1 transition-all duration-300 flex items-center gap-2"
                  >
                    <span className="w-1 h-1 rounded-full bg-brand-saffron opacity-0 group-hover:opacity-100"></span>
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Popular Treatments */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white border-b border-white/10 pb-2 inline-block">Popular Treatments</h4>
            <ul className="space-y-4">
              {[
                "Heart Surgery",
                "Orthopedic Surgery",
                "Cancer Care",
                "Cosmetic Surgery",
                "Dental Care",
                "Ayurveda"
              ].map((item, index) => (
                <li key={index} className="text-gray-400 hover:text-brand-teal transition-colors duration-300 cursor-default">
                  {item}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-bold mb-6 text-white border-b border-white/10 pb-2 inline-block">Contact Us</h4>
            <ul className="space-y-6">
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-brand-saffron" />
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Phone</div>
                  <div className="text-white hover:text-brand-saffron transition-colors cursor-pointer">+91 9660706047</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-brand-saffron" />
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Email</div>
                  <div className="text-white hover:text-brand-saffron transition-colors cursor-pointer">info@bharatheal.com</div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
                  <MapPin className="h-5 w-5 text-brand-saffron" />
                </div>
                <div>
                  <div className="text-sm text-gray-400 mb-1">Location</div>
                  <div className="text-white">4th Floor, PN 3 and 9, Office No. 402, Shree GD Tower, Vaishali Nagar, Jaipur, Rajasthan, 302021, India</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="text-gray-500 text-sm text-center md:text-left">
            © {new Date().getFullYear()} BharatHeal. All rights reserved.
          </div>

          <div className="flex gap-6 text-sm text-gray-500">
            <span className="cursor-pointer hover:text-white transition-colors">Privacy Policy</span>
            <span className="cursor-pointer hover:text-white transition-colors">Terms of Service</span>
          </div>

          <Button
            onClick={scrollToTop}
            variant="outline"
            size="icon"
            className="rounded-full bg-white/5 border-white/10 text-white hover:bg-brand-saffron hover:text-brand-navy hover:border-brand-saffron transition-colors"
          >
            <ArrowUp className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </footer>
  );
};
